/** School-wide term dates (YYYY-MM-DD). Billing still uses the month calendar;
 *  fuel/route charts use these days. */

export function parseYmd(s: string | null | undefined): Date | null {
  if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
  const d = new Date(`${s}T12:00:00`);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Term start that has already begun. Upcoming dates stay on file for the
 *  reminder, but charts must not jump to an empty future window. */
export function activeTermStartYmd(s: string | null | undefined, today = new Date()): string | null {
  const d = parseYmd(s);
  if (!d) return null;
  const t = new Date(today);
  t.setHours(12, 0, 0, 0);
  return d.getTime() <= t.getTime() ? s! : null;
}

export function addDays(d: Date, n: number): Date {
  const x = new Date(d.getTime());
  x.setDate(x.getDate() + n);
  return x;
}

/** Cancelled / cancel-at-period-end / expired schools must not be nagged. */
export function subscriptionAllowsReminders(org: Record<string, unknown> | null | undefined): boolean {
  if (!org) return false;
  if (org.cancelAtPeriodEnd === true) return false;
  const st = String(org.subscriptionStatus || "").toLowerCase();
  if (st === "canceled" || st === "cancelled" || st === "expired" || st === "suspended") return false;
  if (org.hasActiveSubscription === false && org.isPilot !== true && st !== "active" && st !== "pending_payment") {
    return false;
  }
  return true;
}

/**
 * Reminder for the last day of THIS term and the first day of the NEXT.
 * Hidden once both are on file. Timing:
 * - 14 days before a known end date
 * - if they never set an end: last painted month of the current term block
 * - if no calendar either: 10 weeks after start
 */
export function shouldRemindNextTerm(args: {
  today?: Date;
  termStart: string | null | undefined;
  termEnd: string | null | undefined;
  nextTermStart?: string | null | undefined;
  termCalendar?: number[] | null;
}): boolean {
  const today = args.today ?? new Date();
  const start = parseYmd(args.termStart ?? null);
  if (!start) return false;
  const end = parseYmd(args.termEnd ?? null);
  const next = parseYmd(args.nextTermStart ?? null);
  if (end && next && next.getTime() > end.getTime()) return false;

  if (end) {
    if (start.getTime() > end.getTime()) return false;
    const remindFrom = addDays(end, -14);
    return today.getTime() >= remindFrom.getTime();
  }
  const cal = args.termCalendar;
  if (Array.isArray(cal) && cal.length === 12) {
    const m = today.getMonth();
    const cur = Number(cal[m]) || 0;
    if (cur === 0) return false;
    const nxt = Number(cal[(m + 1) % 12]) || 0;
    return nxt !== cur;
  }
  return today.getTime() >= addDays(start, 10 * 7).getTime();
}

/** Chart window: stay on this term until the next start day has actually arrived. */
export function liveTermStartYmd(args: {
  currentStart?: string | null;
  nextStart?: string | null;
  today?: Date;
}): string | null {
  const nextLive = activeTermStartYmd(args.nextStart, args.today);
  if (nextLive) return nextLive;
  return activeTermStartYmd(args.currentStart, args.today);
}

/** Mondays from `fromYmd` through `toYmd` (inclusive), as YYYY-MM-DD week keys. */
export function termWeekKeys(fromYmd: string, toYmd: string): string[] {
  const from = parseYmd(fromYmd);
  const to = parseYmd(toYmd);
  if (!from || !to || to < from) return [];
  const keys: string[] = [];
  const d = new Date(from);
  const day = d.getDay() || 7;
  d.setDate(d.getDate() - day + 1);
  d.setHours(12, 0, 0, 0);
  while (d.getTime() <= to.getTime() + 36 * 3600 * 1000) {
    keys.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 7);
    if (keys.length > 40) break;
  }
  return keys;
}
